import './perks-profile-carousel.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { selectAccountSettings, selectCustomer } from '../../../auth/store/auth.selectors';
import { QRComponent } from '../../../codes/components/qr.component';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { AppleWalletBtn } from '../../../common/components/wallet/apple-wallet-btn.component';
import { GoogleWalletBtn } from '../../../common/components/wallet/google-wallet-btn.component';
import { IconCalendar } from '../../../common/icons/icon-calendar';
import { IconCloseCircle } from '../../../common/icons/icon-close-circle';
import { IconPencil } from '../../../common/icons/icon-pencil';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { WalletType } from '../../../membership/models/membership.model';
import { membershipService } from '../../../membership/services/membership.service';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';
import { isAndroidMobile, isMobile } from '../../../utils/platform.utils';
import { selectUser } from '../../store/perks.selectors';
import { updateUser, uploadAvatar } from '../../store/perks.thunk';
import { PerksAvatar } from '../perks-avatar/perks-avatar.component';

const formatPhone = (raw: string): string => {
  let value = raw.replace(/\D/g, '');
  if (value.length > 10) value = value.slice(0, 10);
  const parts: string[] = [];
  if (value.length >= 1) parts.push('(' + value.slice(0, 3));
  if (value.length >= 4) parts.push(') ' + value.slice(3, 6));
  if (value.length >= 7) parts.push('-' + value.slice(6, 10));
  return parts.join('');
};

const splitPhoneNumber = (phone: string | undefined) => {
  if (!phone) return null;
  const trimmed = phone.replace(/\D/g, '');
  if (trimmed.length === 0) return null;
  if (trimmed.length <= 10) {
    return { countryCode: '+1', localNumber: formatPhone(trimmed) };
  }
  const localNumber = trimmed.slice(-10);
  const countryCode = phone.slice(0, phone.length - 10);
  return { countryCode, localNumber: formatPhone(localNumber) };
};

interface FormData {
  username: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
  birthday: string;
  zipCode: string;
}

export const PerksProfileCarousel = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const customer = useAppSelector(selectCustomer);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const memberships = useAppSelector(selectMemberships);
  // The artist is not a fan: their name and avatar come from the official-author record, so
  // "complete your profile" has nothing for them to fill in. Behind community-official-authors,
  // like the rest of the author identity -- with the flag off they are an ordinary member here.
  const officialAuthorsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  const isOfficialAuthor = officialAuthorsEnabled && !!user?.officialAuthor;

  const hasSelectedTier = memberships?.some((m) => !!m.selectedTier);

  const contract =
    memberships?.find((m) => m.contract?.tier?.walletTheme?.enabled)?.contract ?? null;
  const walletEnabled = !!contract?.tier?.walletTheme?.enabled;

  const todayMinus18 = (() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split('T')[0];
  })();

  const [activeSlide, setActiveSlide] = useState(0);
  // Dismiss state is keyed per customer so one account's dismissal doesn't
  // hide the carousel for another account on the same browser (SNGL-6959).
  // Defaults to dismissed until the customer loads to avoid flashing cards
  // that were already dismissed for this account.
  const customerId = customer?.customerId;
  const profileDismissedKey = `sng_profile_card_dismissed_${customerId}`;
  const walletDismissedKey = `sng_wallet_card_dismissed_${customerId}`;
  const [profileDismissed, setProfileDismissed] = useState(true);
  const [walletDismissed, setWalletDismissed] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    setProfileDismissed(localStorage.getItem(profileDismissedKey) === 'true');
    setWalletDismissed(localStorage.getItem(walletDismissedKey) === 'true');
    // Legacy browser-scoped keys; intentionally not migrated so each account
    // re-evaluates its own dismiss state.
    localStorage.removeItem('sng_profile_card_dismissed');
    localStorage.removeItem('sng_wallet_card_dismissed');
  }, [customerId]);
  const [formData, setFormData] = useState<FormData>({
    username: '',
    firstName: '',
    lastName: '',
    countryCode: '',
    phone: '',
    birthday: '',
    zipCode: '',
  });
  const [walletPassUrl, setWalletPassUrl] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      const phoneParts = splitPhoneNumber(user.phone);
      setFormData({
        username: user.username || '',
        firstName: user.firstName || customer?.firstName || '',
        lastName: user.lastName || customer?.lastName || '',
        countryCode: phoneParts?.countryCode || '',
        phone: phoneParts?.localNumber || '',
        birthday: user.birthday || '',
        zipCode: user.zipCode || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (!walletEnabled || walletDismissed || !contract) return;
    if (isMobile()) {
      const walletType = isAndroidMobile() ? WalletType.GOOGLE : WalletType.APPLE;
      membershipService.getWalletPassUrl(contract, walletType).then((result) => {
        setWalletPassUrl(result.url);
      });
    } else {
      // Desktop renders a QR scanned by an unknown phone. Encode the app-proxy
      // scan URL directly: the backend resolves Apple vs Google from the
      // SCANNING phone's User-Agent and creates the pass at scan time.
      setWalletPassUrl(
        `${window.location.origin}/apps/single/v3/wallet/download/contract/${contract.id}`
      );
    }
    // walletDismissed starts true and flips once the customer loads, so it
    // must re-trigger this fetch.
  }, [walletEnabled, walletDismissed, contract?.id]);

  // Saved completeness — based on persisted user data, controls whether card is shown
  const savedProgressFields: boolean[] = [
    !!user?.username,
    ...(accountSettings?.showPhoneField ? [!!user?.phone] : []),
    ...(accountSettings?.showZipCodeField ? [!!user?.zipCode] : []),
    ...(accountSettings?.showBirthdayField ? [!!user?.birthday] : []),
  ];
  const isSavedProfileComplete =
    savedProgressFields.length > 0 ? savedProgressFields.every(Boolean) : true;

  // Live progress — based on form data, drives the progress bar only
  const progressFields: boolean[] = [
    !!formData.username,
    ...(accountSettings?.showPhoneField ? [!!formData.phone] : []),
    ...(accountSettings?.showZipCodeField ? [!!formData.zipCode] : []),
    ...(accountSettings?.showBirthdayField ? [!!formData.birthday] : []),
  ];
  const progressTotal = progressFields.length;
  const progressFilled = progressFields.filter(Boolean).length;
  const progressPercent =
    progressTotal > 0 ? Math.round((progressFilled / progressTotal) * 100) : 100;
  const isProfileComplete = progressPercent === 100;

  const showProfile =
    !profileDismissed &&
    !isSavedProfileComplete &&
    !!user &&
    !isOfficialAuthor &&
    accountSettings?.showCompleteProfileCard !== false;
  const showWallet =
    !walletDismissed && walletEnabled && accountSettings?.showClaimWalletPass !== false;
  const visibleCount = (showProfile ? 1 : 0) + (showWallet ? 1 : 0);

  if (!hasSelectedTier || visibleCount === 0) return <></>;

  const cards: Array<'profile' | 'wallet'> = [];
  if (showProfile) cards.push('profile');
  if (showWallet) cards.push('wallet');

  const dismiss = (card: 'profile' | 'wallet') => {
    const key = card === 'profile' ? profileDismissedKey : walletDismissedKey;
    localStorage.setItem(key, 'true');
    if (card === 'profile') {
      setProfileDismissed(true);
    } else {
      setWalletDismissed(true);
    }
    setActiveSlide(0);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneInput = (e: Event) => {
    handleInputChange('phone', formatPhone((e.target as HTMLInputElement).value));
  };

  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarImageLoading, setAvatarImageLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputClick = () => fileInputRef.current?.click();

  const handleUploadAvatar = async (file: File | undefined) => {
    if (!file || !user) return;
    const filename = file.name.replace(/[^a-zA-Z0-9.]/g, '-');
    setIsUploading(true);
    const result = await dispatch(uploadAvatar({ file, filename }));
    setIsUploading(false);
    if (uploadAvatar.rejected.match(result)) {
      toastService.error(t('common.error'), t('perks.errorUploadAvatar'));
    } else if (uploadAvatar.fulfilled.match(result)) {
      setAvatarImageLoading(true);
    }
  };

  const handleSave = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!user || saving) return;

    setSaving(true);
    let fullPhoneNum = '';
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const cc = formData.countryCode.startsWith('+')
        ? formData.countryCode
        : `+${formData.countryCode}`;
      fullPhoneNum = `${cc}${phoneDigits}`;
    }

    const result = await dispatch(
      updateUser({
        username: formData.username,
        imageFileSetId: user.imageFileSetId,
        emailPreferences: user.emailPreferences || [],
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: fullPhoneNum,
        birthday: formData.birthday,
        zipCode: formData.zipCode,
      })
    );

    setSaving(false);

    if (updateUser.fulfilled.match(result)) {
      toastService.success('Profile updated', 'Your profile has been saved.');
      if (isProfileComplete && !showWallet) {
        dismiss('profile');
      }
    }
  };

  const carouselRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchDeltaX = useRef<number>(0);
  const directionLocked = useRef<'h' | 'v' | null>(null);
  const activeSlideRef = useRef(activeSlide);
  activeSlideRef.current = activeSlide;
  const cardsLengthRef = useRef(cards.length);
  cardsLengthRef.current = cards.length;

  useEffect(() => {
    const card = trackRef.current?.children[activeSlide] as HTMLElement | undefined;
    if (!card) return;

    const updateHeight = () => {
      requestAnimationFrame(() => {
        const c = trackRef.current?.children[activeSlide] as HTMLElement | undefined;
        if (c) setViewportHeight(c.offsetHeight);
      });
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(card);
    return () => observer.disconnect();
  }, [activeSlide, cards.length]);

  // Scroll carousel into view when slide changes so the user isn't left
  // below a shorter card (e.g. wallet card after swiping from profile card).
  useEffect(() => {
    carouselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeSlide]);

  // Attach touchmove as non-passive so we can preventDefault for horizontal
  // swipes, preventing the browser's back/forward navigation gesture.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      touchDeltaX.current = 0;
      directionLocked.current = null;
    };

    const onMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = e.touches[0].clientY - touchStartY.current;
      touchDeltaX.current = dx;
      if (!directionLocked.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        directionLocked.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }
      if (directionLocked.current === 'h') {
        // preventDefault stops the browser's own gestures, but the event
        // still bubbles to the host theme's document-level touch listeners,
        // whose drawer opens on horizontal swipes (SNGL-7198). Stop it here
        // so a swipe inside the carousel only ever navigates the carousel.
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (directionLocked.current === 'h') {
        e.stopPropagation();
        const threshold = 50;
        const cur = activeSlideRef.current;
        const total = cardsLengthRef.current;
        if (touchDeltaX.current < -threshold && cur < total - 1) {
          setActiveSlide(cur + 1);
        } else if (touchDeltaX.current > threshold && cur > 0) {
          setActiveSlide(cur - 1);
        }
      }
      touchDeltaX.current = 0;
      directionLocked.current = null;
    };

    track.addEventListener('touchstart', onStart, { passive: true });
    track.addEventListener('touchmove', onMove, { passive: false });
    track.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      track.removeEventListener('touchstart', onStart);
      track.removeEventListener('touchmove', onMove);
      track.removeEventListener('touchend', onEnd);
    };
  }, []);

  return (
    <div ref={carouselRef} class="single-profile-carousel">
      <div
        class="single-carousel-viewport"
        style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}
      >
        <div
          ref={trackRef}
          class="single-carousel-track"
          style={{ transform: `translateX(-${activeSlide * 100}%)` }}
        >
          {cards.map((card) =>
            card === 'profile' ? (
              <div class="single-carousel-card single-profile-card" key="profile">
                <button
                  class="single-dismiss-btn"
                  type="button"
                  aria-label={t('a11y.dismiss')}
                  onClick={() => dismiss('profile')}
                >
                  <IconCloseCircle />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif"
                  hidden
                  style="display: none !important"
                  onChange={(e) => handleUploadAvatar((e.target as HTMLInputElement).files?.[0])}
                />
                <div class="single-profile-card-header">
                  <div class="single-profile-avatar-wrapper">
                    {user && (
                      <PerksAvatar
                        user={user}
                        size={60}
                        onImageLoad={() => setAvatarImageLoading(false)}
                      />
                    )}
                    {(isUploading || avatarImageLoading) && (
                      <div class="single-avatar-loading-overlay">
                        <SpinnerComponent />
                      </div>
                    )}
                    <span class="single-edit-container" onClick={handleFileInputClick}>
                      <button class="button btn" type="button" aria-label={t('a11y.uploadAvatar')}>
                        <IconPencil width={11} height={11} />
                      </button>
                    </span>
                  </div>
                  <div class="single-profile-title-wrapper">
                    <h4 class="single-card-title">{t('perks.profileCardTitle')}</h4>
                    <small>{t('perks.profileVisibilityNote')}</small>
                  </div>
                </div>
                <form class="single-profile-form" onSubmit={(e) => e.preventDefault()}>
                  <div class="single-form-grid">
                    <div class="single-input-group">
                      <label htmlFor="single-profile-username">{t('perks.displayName')}</label>
                      <input
                        id="single-profile-username"
                        type="text"
                        placeholder={user?.username || t('perks.usernamePlaceholder')}
                        value={formData.username}
                        onInput={(e) =>
                          handleInputChange('username', (e.target as HTMLInputElement).value)
                        }
                      />
                    </div>
                    {accountSettings?.showFirstName && (
                      <div class="single-input-group">
                        <label htmlFor="single-profile-first-name">{t('auth.firstName')}</label>
                        <input
                          id="single-profile-first-name"
                          type="text"
                          value={formData.firstName}
                          onInput={(e) =>
                            handleInputChange('firstName', (e.target as HTMLInputElement).value)
                          }
                        />
                      </div>
                    )}
                    {accountSettings?.showLastName && (
                      <div class="single-input-group">
                        <label htmlFor="single-profile-last-name">{t('auth.lastName')}</label>
                        <input
                          id="single-profile-last-name"
                          type="text"
                          value={formData.lastName}
                          onInput={(e) =>
                            handleInputChange('lastName', (e.target as HTMLInputElement).value)
                          }
                        />
                      </div>
                    )}
                    {accountSettings?.showPhoneField && (
                      <div class="single-input-group">
                        <label htmlFor="single-profile-phone">{t('auth.phoneNumber')}</label>
                        <div class="single-phone-group">
                          <input
                            type="text"
                            aria-label={t('a11y.countryCode')}
                            class="single-country-code"
                            value={formData.countryCode}
                            placeholder="+1"
                            maxLength={4}
                            style={{
                              width: `calc(${Math.max(2, formData.countryCode?.length ?? 0)}ch + 1rem)`,
                            }}
                            onInput={(e) =>
                              handleInputChange('countryCode', (e.target as HTMLInputElement).value)
                            }
                          />
                          <input
                            id="single-profile-phone"
                            type="text"
                            value={formData.phone}
                            placeholder={t('auth.phonePlaceholder')}
                            onInput={handlePhoneInput}
                          />
                        </div>
                      </div>
                    )}
                    {accountSettings?.showZipCodeField && (
                      <div class="single-input-group">
                        <label htmlFor="single-profile-zip">{t('auth.zipcodePlaceholder')}</label>
                        <input
                          id="single-profile-zip"
                          type="text"
                          value={formData.zipCode}
                          placeholder="12345"
                          onInput={(e) =>
                            handleInputChange('zipCode', (e.target as HTMLInputElement).value)
                          }
                        />
                      </div>
                    )}
                    {accountSettings?.showBirthdayField && (
                      <div class="single-input-group">
                        <label htmlFor="single-profile-birthday">{t('auth.birthday')}</label>
                        <div class="single-date-wrapper">
                          <input
                            id="single-profile-birthday"
                            type="date"
                            value={formData.birthday}
                            max={todayMinus18}
                            onInput={(e) =>
                              handleInputChange('birthday', (e.target as HTMLInputElement).value)
                            }
                          />
                          <span class="single-date-icon" aria-hidden="true">
                            <IconCalendar width={14} height={14} />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div class="single-profile-form-footer">
                    <button
                      type="button"
                      class="button btn single-save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? t('perks.updatingProfile') : t('perks.updateProfile')}
                    </button>
                  </div>
                </form>
                <div class="single-profile-progress">
                  <div
                    class="single-profile-progress-bar"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            ) : (
              <div class="single-carousel-card single-wallet-card" key="wallet">
                <button
                  class="single-dismiss-btn"
                  type="button"
                  aria-label={t('a11y.dismiss')}
                  onClick={() => dismiss('wallet')}
                >
                  <IconCloseCircle />
                </button>
                <div class="single-wallet-inner">
                  {!isMobile() && (
                    <div class="single-wallet-qr">
                      {walletPassUrl && <QRComponent text={walletPassUrl} />}
                    </div>
                  )}
                  <div class="single-wallet-content">
                    <h4 class="single-card-title">{t('perks.walletCardTitle')}</h4>
                    <p class="single-wallet-pass-desc">{t('perks.walletPassDescription')}</p>
                    {(!isMobile() || !walletPassUrl) && (
                      <>
                        <br />
                        <p>{t('perks.walletScanDescription')}</p>
                      </>
                    )}
                    {isMobile() && walletPassUrl && (
                      <div class="single-wallet-mobile-btn">
                        {isAndroidMobile() ? (
                          <GoogleWalletBtn
                            onClick={() => {
                              window.location.href = walletPassUrl;
                            }}
                          />
                        ) : (
                          <AppleWalletBtn
                            onClick={() => {
                              window.location.href = walletPassUrl;
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      {visibleCount > 1 && (
        <div class="single-carousel-dots">
          {cards.map((_, i) => (
            <button
              key={i}
              type="button"
              class={`single-dot ${activeSlide === i ? 'active' : ''}`}
              onClick={() => setActiveSlide(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
